import isEmpty from 'lodash/isEmpty';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Overlay } from '@/components/Overlay';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import { generateDataTestId } from '@/lib/tests';
import type { WithChildren, WithHTMLProp } from '@/lib/types';
import type { BreakpointsKeys } from '@/theme/types';

import * as S from './Modal.styles';

export interface ModalProps {
  alignItems?: string;
  children?: ReactNode;
  className?: string;
  ecsClose?: boolean;
  gutters?: boolean;
  height?: string;
  isCreatePortal?: boolean;
  isOpen?: boolean;
  maxWidth?: BreakpointsKeys;
  onClose?: () => void;
  overflowHidden?: boolean;
  rootId?: string;
  screenClose?: boolean;
  scrollToTopWhenOpen?: boolean;
  unmountOnClose?: boolean;
  width?: string;
}

export const Modal = ({
  alignItems,
  children,
  className,
  ecsClose = true,
  gutters = true,
  height = '',
  isCreatePortal = true,
  isOpen = false,
  maxWidth = 'sm',
  onClose,
  overflowHidden = false,
  rootId = 'modal-root',
  screenClose = true,
  scrollToTopWhenOpen,
  unmountOnClose = true,
  width = '',
}: ModalProps) => {
  const modalTargetElement = useRef<HTMLElement | null>(null);
  const { isMobile } = useDeviceDetect();
  const isOverlayClose = screenClose && !isMobile;

  useEffect(() => {
    if (isCreatePortal) {
      modalTargetElement.current = document.getElementById(rootId);
    }
  }, [isCreatePortal]);

  useLockBodyScroll(isOpen, scrollToTopWhenOpen);

  useEffect(() => {
    if (!ecsClose) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [ecsClose, isOpen]);

  const portalContent = (
    <Overlay
      alignItems={alignItems}
      isFullWidthMobile={isEmpty(width)}
      onClick={isOverlayClose ? onClose : undefined}
    >
      <S.Wrapper
        $height={height}
        $maxWidth={maxWidth}
        $overflowHidden={overflowHidden}
        $width={width}
        aria-modal
        className={className}
        gutters={gutters}
      >
        {children}
      </S.Wrapper>
    </Overlay>
  );

  if (!isCreatePortal) {
    return (
      <Overlay
        alignItems={alignItems}
        onClick={onClose}
        style={{
          pointerEvents: isOpen ? 'all' : 'none',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <S.Wrapper
          $height={height}
          $maxWidth={maxWidth}
          $overflowHidden={overflowHidden}
          $width={width}
          aria-modal
          className={className}
          gutters={gutters}
          style={{
            isolation: 'isolate',
            pointerEvents: isOpen ? 'all' : 'none',
            opacity: isOpen ? 1 : 0,
          }}
        >
          {children}
        </S.Wrapper>
      </Overlay>
    );
  }

  // TODO: Refactor it better
  if (!unmountOnClose && modalTargetElement.current) {
    return createPortal(
      <Overlay
        alignItems={alignItems}
        onClick={isOverlayClose ? onClose : undefined}
        style={{
          pointerEvents: isOpen ? 'all' : 'none',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <S.Wrapper
          $height={height}
          $maxWidth={maxWidth}
          $overflowHidden={overflowHidden}
          $width={width}
          aria-modal
          className={className}
          gutters={gutters}
          style={{
            isolation: 'isolate',
            pointerEvents: isOpen ? 'all' : 'none',
            opacity: isOpen ? 1 : 0,
          }}
        >
          {children}
        </S.Wrapper>
      </Overlay>,
      modalTargetElement.current,
    );
  }

  if (!modalTargetElement.current || !isOpen) {
    return null;
  }

  return createPortal(portalContent, modalTargetElement.current);
};

interface ModalHeaderProps {
  children?: ReactNode;
  divider?: boolean;
  onClose?: () => void;
}

const ModalHeader = ({ children, divider, onClose }: ModalHeaderProps) => (
  <>
    <S.Header
      className='ModalHeader'
      {...generateDataTestId('modal', 'header')}
    >
      {children}
      {onClose && (
        <S.Close
          aria-label='Close modal'
          iconProps={{ name: 'close', variant: 'regular' }}
          onClick={onClose}
          size='s'
          variant='ghosted'
          {...generateDataTestId('modal', 'close')}
        />
      )}
    </S.Header>
    {divider && <S.Divider />}
  </>
);

type ModalBodyProps = WithChildren & WithHTMLProp<'className'>;
const ModalBody = ({ children, className }: ModalBodyProps) => (
  <S.Body {...generateDataTestId('modal', 'body')} className={className}>
    {children}
  </S.Body>
);

type ModalFooterProps = WithChildren &
  WithHTMLProp<'className'> & { isFullWidthMobile?: boolean };
const ModalFooter = ({
  children,
  className,
  isFullWidthMobile = true,
}: ModalFooterProps) => (
  <S.Footer className={className} isFullWidthMobile={isFullWidthMobile}>
    {children}
  </S.Footer>
);

type ModalTailProps = WithChildren & WithHTMLProp<'className'>;
const ModalTail = ({ children, className }: ModalTailProps) => (
  <S.TailWrapper className={className}>
    {children}
    <S.Tail />
  </S.TailWrapper>
);

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Tail = ModalTail;

Modal.displayName = 'Modal';
